import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogOut, User as UserIcon, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { NAV_LINKS } from "../data/content";
import { useAuth } from "../lib/auth";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-zinc-200" : "bg-white/0 border-b border-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl h-16 flex items-center justify-between">
        <Logo testId="nav-logo" />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-zinc-950" : "text-zinc-500 hover:text-zinc-950"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-chip"
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-2 py-1 pr-2.5 hover:border-zinc-950 focus:outline-none focus:ring-2 focus:ring-zinc-300"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-zinc-950 text-[10px] font-semibold text-white">
                      {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span className="text-sm font-medium text-zinc-800 max-w-[120px] truncate">
                    {user.name?.split(" ")[0] || "Account"}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-xs text-zinc-500">Signed in as</div>
                  <div className="truncate text-sm font-medium text-zinc-950">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="nav-menu-app">
                  <Link to="/try"><UserIcon className="mr-2 h-4 w-4" /> Open the app</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-menu-contact">
                  <Link to="/contact">Contact support</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="nav-logout"
                  onClick={logout}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-cta-secondary">
                <Button variant="ghost" className="text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100">Sign in</Button>
              </Link>
              <Link to="/try" data-testid="nav-cta-primary">
                <Button className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-full px-5">Try free</Button>
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-md text-zinc-900 hover:bg-zinc-100"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium ${
                    isActive ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to={user ? "/try" : "/login"} data-testid="mobile-nav-cta">
              <Button className="mt-2 w-full bg-zinc-950 text-white hover:bg-zinc-800 rounded-full">{user ? "Open app" : "Sign in"}</Button>
            </Link>
            {user && (
              <>
                <div className="mt-3 border-t border-zinc-200 pt-3">
                  <div className="px-3 text-[11px] uppercase tracking-wider text-zinc-500">Signed in as</div>
                  <div className="truncate px-3 text-sm font-medium text-zinc-900">{user.email}</div>
                </div>
                <Button
                  variant="outline"
                  data-testid="mobile-nav-logout"
                  onClick={logout}
                  className="mt-2 w-full rounded-full border-zinc-300 text-red-600 hover:bg-red-50 hover:text-red-600"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
